import React, { useEffect, useState } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useGetSphereInfoQuery } from '@center-pwa/store/sphereInfoAPI';

interface SphereNameInlineProps {
  sphereId: string;
  /**
   * Text to render while the name is resolving OR when resolution fails.
   * Absent = render nothing during the transient period; the surrounding
   * text stays intact but reflows once the name arrives.
   */
  fallback?: string;
  className?: string;
}

/**
 * Inline text-only rendering of a sphere's display name.
 *
 * Complements `AtlasSphereIdentityMolecule` which renders a full avatar +
 * name row. `SphereNameInline` is used when the name needs to appear
 * mid-sentence (e.g. "One status for this factor, set at the latest
 * check-in (Mike Hicks · 4h ago)") — the row-shaped molecule breaks
 * paragraph flow there.
 *
 * Same data source as AtlasSphereIdentityMolecule so the cache is shared.
 */
const SphereNameInline: React.FC<SphereNameInlineProps> = ({
  sphereId,
  fallback = '',
  className,
}) => {
  const { getEffectiveToken } = useImpersonation();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const t = await getEffectiveToken();
      setToken(t ?? null);
    })();
  }, [getEffectiveToken]);

  const skip = !token;
  const { data: sphereInfo, isLoading, isError } = useGetSphereInfoQuery(
    { sphereId, token: token || '' },
    { skip },
  );

  if (!token || isLoading) {
    return fallback ? <span className={className}>{fallback}</span> : null;
  }
  if (isError || !sphereInfo?.sphere) {
    return fallback ? <span className={className}>{fallback}</span> : null;
  }

  const name = sphereInfo.sphere.properties?.name;
  if (!name) return fallback ? <span className={className}>{fallback}</span> : null;

  return <span className={className}>{name}</span>;
};

export default SphereNameInline;
